var _a, _b, _c;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useId, useState } from "react";
import { Box, Button, Chip, Popover, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import { EScheduledOutcomeCheckId, EScheduledOutcomeKind, } from "@sequentech/ui-core";
import { useTimeZoneService } from "./timeZoneService";
/** The translation key part of each outcome and check (the wire values are kebab-case). */
export var OUTCOME_KEY = (_a = {},
    _a[EScheduledOutcomeKind.RUNS] = "runs",
    _a[EScheduledOutcomeKind.RUNS_UNSIGNED] = "runsUnsigned",
    _a[EScheduledOutcomeKind.REFUSED] = "refused",
    _a[EScheduledOutcomeKind.WAITING_FOR_INITIALIZATION] = "waitingForInitialization",
    _a);
export var CHECK_KEY = (_b = {},
    _b[EScheduledOutcomeCheckId.NEEDS_SIGNATURES] = "needsSignatures",
    _b[EScheduledOutcomeCheckId.COVERED] = "covered",
    _b[EScheduledOutcomeCheckId.UNSIGNED_CLOSE] = "unsignedClose",
    _b[EScheduledOutcomeCheckId.STRICTER_COPY] = "stricterCopy",
    _b[EScheduledOutcomeCheckId.DEFAULTS] = "defaults",
    _b[EScheduledOutcomeCheckId.INITIALIZATION] = "initialization",
    _b[EScheduledOutcomeCheckId.VOTING_CLOSE] = "votingClose",
    _b);
var OUTCOME_COLOR = (_c = {},
    _c[EScheduledOutcomeKind.RUNS] = "success",
    _c[EScheduledOutcomeKind.RUNS_UNSIGNED] = "warning",
    _c[EScheduledOutcomeKind.REFUSED] = "error",
    _c[EScheduledOutcomeKind.WAITING_FOR_INITIALIZATION] = "warning",
    _c);
/** White on the warning colour is too faint to read (axe color-contrast): dark text. */
export var WARNING_TEXT = {
    warning: { color: "common.black" },
};
/** Parameters named `…_at` or `…_date` are RFC 3339 instants: shown in the row's zone. */
var INSTANT_PARAM = /(_at|_date)$/;
/** A check value or next step in words: its message key with its parameters. */
export var valueText = function (value, zone, service) {
    var _a;
    if (!value)
        return "–";
    var params = {};
    for (var _i = 0, _b = Object.entries((_a = value.params) !== null && _a !== void 0 ? _a : {}); _i < _b.length; _i++) {
        var _c = _b[_i], name_1 = _c[0], raw = _c[1];
        params[name_1] =
            INSTANT_PARAM.test(name_1) && typeof raw === "string" && !Number.isNaN(Date.parse(raw))
                ? service.formatDateTimeZone(raw, zone, service.text)
                : Array.isArray(raw)
                    ? raw.join(", ")
                    : raw;
    }
    // A number of signatures is plural: i18next reads `count`.
    if (typeof params.signatures === "number" && params.count === undefined) {
        params.count = params.signatures;
    }
    return String(service.text.t(value.message_key, params));
};
/**
 * The one-line note of an outcome (design §5a): who authorized it, or what
 * keeps it from running.
 */
export var outcomeNote = function (explanation, t) {
    var _a;
    switch (explanation.outcome) {
        case EScheduledOutcomeKind.RUNS:
            return explanation.authorized_by
                ? t("scheduledOutcome.note.authorized", { code: explanation.authorized_by.code })
                : t("scheduledOutcome.note.noSignaturesNeeded");
        case EScheduledOutcomeKind.RUNS_UNSIGNED:
            return t("scheduledOutcome.note.closesUnsigned");
        case EScheduledOutcomeKind.WAITING_FOR_INITIALIZATION:
            return t("scheduledOutcome.note.waitingForInitialization");
        case EScheduledOutcomeKind.REFUSED:
            // Why, then what would change it, as the server says (e.g. require the approval first).
            return t("scheduledOutcome.note.refusedWithStep", {
                reason: t("scheduledOutcome.note.refused.".concat(CHECK_KEY[explanation.deciding])),
                next: t(explanation.next_step.message_key, (_a = explanation.next_step.params) !== null && _a !== void 0 ? _a : {}),
            });
    }
};
/** The checks table of an explanation: each question, both copies, and its verdict. */
export var OutcomeChecks = function (_a) {
    var explanation = _a.explanation, zone = _a.zone;
    var t = useTranslation().t;
    var service = useTimeZoneService();
    var row = function (check) {
        var deciding = check.id === explanation.deciding;
        return (_jsxs(TableRow, { "data-testid": "outcome-check-".concat(check.id), selected: deciding, "aria-current": deciding ? "true" : undefined, children: [_jsxs(TableCell, { component: "th", scope: "row", sx: { fontWeight: deciding ? 600 : 400 }, children: [t("scheduledOutcome.question.".concat(CHECK_KEY[check.id])), deciding ? (_jsx(Chip, { size: "small", sx: { ml: 1 }, label: t("scheduledOutcome.why.deciding"), variant: "outlined" })) : null] }), _jsx(TableCell, { children: valueText(check.current, zone, service) }), _jsx(TableCell, { children: valueText(check.published, zone, service) }), _jsx(TableCell, { children: _jsxs(Box, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [check.allows ? (_jsx(CheckCircleOutlineIcon, { fontSize: "small", color: "success", "aria-hidden": true })) : (_jsx(BlockIcon, { fontSize: "small", color: "error", "aria-hidden": true })), t(check.allows
                                ? "scheduledOutcome.why.allows"
                                : "scheduledOutcome.why.blocks")] }) })] }, check.id));
    };
    return (_jsxs(Stack, { spacing: 1.5, children: [_jsxs(Table, { size: "small", "aria-label": t("scheduledOutcome.why.checks"), children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("scheduledOutcome.why.check") }), _jsx(TableCell, { children: t("scheduledOutcome.why.current") }), _jsx(TableCell, { children: t("scheduledOutcome.why.published") }), _jsx(TableCell, { children: t("scheduledOutcome.why.verdict") })] }) }), _jsx(TableBody, { children: explanation.checks.map(row) })] }), _jsxs(Typography, { variant: "body2", children: [_jsx(Box, { component: "span", sx: { fontWeight: 600 }, children: t("scheduledOutcome.why.nextStep") }), " ", valueText(explanation.next_step, zone, service)] })] }));
};
/** The outcome chip of a scheduled transition ("Will run", "Will run without signatures", "Will be refused"). */
export var OutcomeChip = function (_a) {
    var outcome = _a.outcome;
    var t = useTranslation().t;
    return (_jsx(Chip, { size: "small", color: OUTCOME_COLOR[outcome], sx: WARNING_TEXT[OUTCOME_COLOR[outcome]], label: t("scheduledOutcome.chip.".concat(OUTCOME_KEY[outcome])) }));
};
/**
 * What a scheduled opening or closing will do and why (design §5c): the
 * outcome chip with its note, and "Why?", which opens the checks table, the
 * deciding check and the next step.
 */
export var ScheduledOutcome = function (_a) {
    var _b;
    var explanation = _a.explanation, zone = _a.zone;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var _d = useState(null), anchor = _d[0], setAnchor = _d[1];
    var titleId = useId();
    return (_jsxs(Stack, { spacing: 0.5, sx: { alignItems: "flex-start" }, "data-testid": "scheduled-outcome", children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "center" }, children: [_jsx(OutcomeChip, { outcome: explanation.outcome }), _jsx(Button, { size: "small", variant: "text", sx: {
                            "&&": {
                                "minWidth": 0,
                                "minHeight": 28,
                                "px": 0.75,
                                "py": 0.25,
                                "fontSize": "0.8125rem",
                                "textTransform": "none",
                                "color": "primary.dark",
                                "bgcolor": "transparent",
                                "border": 0,
                                "boxShadow": "none",
                                "&:hover, &:focus, &:active": {
                                    color: function (theme) { return "".concat(theme.palette.primary.dark, " !important"); },
                                    bgcolor: "transparent !important",
                                    border: 0,
                                    boxShadow: "none",
                                    textDecoration: "underline",
                                },
                                "&.Mui-focusVisible": { outline: "2px solid", outlineOffset: 2 },
                            },
                        }, "aria-haspopup": "dialog", "aria-expanded": !!anchor, onClick: function (event) { return setAnchor(event.currentTarget); }, children: t("scheduledOutcome.why.button") })] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: outcomeNote(explanation, t) }), _jsx(Popover, { open: !!anchor, anchorEl: anchor, onClose: function () { return setAnchor(null); }, anchorOrigin: { vertical: "bottom", horizontal: "left" }, slotProps: {
                    paper: {
                        "role": "dialog",
                        "aria-labelledby": titleId,
                        "sx": { p: 2, maxWidth: 720 },
                    },
                }, children: _jsxs(Stack, { spacing: 1.5, children: [_jsx(Typography, { id: titleId, component: "h3", sx: { fontWeight: 600 }, children: t("scheduledOutcome.why.title.".concat(OUTCOME_KEY[explanation.outcome])) }), _jsx(Typography, { variant: "body2", children: outcomeNote(explanation, t) }), ((_b = explanation.authorized_by) === null || _b === void 0 ? void 0 : _b.signers.length) ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("scheduledOutcome.why.signedBy", {
                                names: new Intl.ListFormat(i18n.language, {
                                    type: "conjunction",
                                }).format(explanation.authorized_by.signers),
                            }) })) : null, _jsx(OutcomeChecks, { explanation: explanation, zone: zone })] }) })] }));
};
